import { Injectable, inject } from "@angular/core";
import { Observable, shareReplay, map } from "rxjs";
import { AppSettingsService } from "../../../core";
import { HttpClient, HttpHeaders, HttpResponse, HttpParams } from "@angular/common/http";
import { IBucketItemRequest } from "../models/bucket-item-request.model";
import { ICreateFolderRequest } from "../models/create-folder-request.model";
import { IBucketItemTransferRequest } from "../models/bucket-item-transfer.model";
import { ITags } from "../models/bucket-item.model";
import { IUpdateStudyCompletionDateRequest } from "../models/UpdateStudyCompletionDate-request.model";
import { ICommitRequest } from "../models/commit-request.model";
import { ITransferPackageResponse } from "../models/transfer-package-response";
import { IRenameFolderRequest } from "../models/rename-folder-request.model";
import { HttpUrlEncodingCodec } from "@angular/common/http";
import { IMoveItems } from "../models/move-items.model";
import { IBucketItemRequestForMove } from "../models/bucket-item-list-request-move.model";
import { IDeleteFolderRequest } from "../models/delete-folder-request.model";
import { IRegenerateChecksum } from "../models/regenerate-checksum-request.model";
import { IMoveResponse } from "../models/move-response.model";
import {
  IFileVersionHistoryRequest,
  IVersion,
} from "../models/file-version-history.model";
import { IUpdateTag, IUpdateTagResponse } from "../models/update-tag.model";
import { ISearchRequest } from "../../models/searchRequest.model";
import { IFileVersionChecksum } from "../models/file-version-checksum.model";
import { IDownloadFileversion } from "../models/download-file-version.model";
import { ISearchResultsResponse } from "../models/search-results-response.model";
import { IRenameFolderResponse } from "../models/rename-folder-response.model";
import { IUpdateStudyCompletionDateResponse } from "../models/update-completion-date-response.model";
import { IExportContentListRequest } from "../models/export-content-list-request.model";
import { IAuditTrailRequest } from "../../audit-trail/models/auditTrailRequest.model";
import { IAuditsResponse } from "../../audit-trail/models/get-audits.model";
import { ILargeFileDownloadResponse, IDownloadItem, ILargeFileDownloadExpandedRequest, IDownloadAuditTrailEntry } from '../models/download-item.model';
import { IDownloadMaliciousFilesRequestDto, IMaliciousFilesResponse } from "../../malicious-files/models/malicious-files.model";
import { ITemplates } from "../models/templates.model";
import { IBucketItemsResponse } from "../models/bucket-items-response.model";

@Injectable({
  providedIn:"any"
})
export class BucketItemService {
  private appSettings = inject(AppSettingsService);
  private http = inject(HttpClient);

  private readonly baseUrl = `${this.appSettings.baseApiUrl}/api/BucketItem`;
  private readonly largeFileDownloadUrl = `${this.appSettings.baseApiUrl}/api/LargeFileDownload`;

  public GetBucketItems(
    requestData: Partial<IBucketItemRequest>,
    page: number = 1
  ): Observable<IBucketItemsResponse> {
    const codec = new HttpUrlEncodingCodec();
    let directory = codec.encodeValue(requestData.DirectoryName);
    let url = `${this.baseUrl}/get-bucket-item-list?bucketId=${requestData.BucketId}&buCode=${requestData.BUCode}&bucketType=${requestData.BucketType}&directoryName=${directory}&pageNumber=${page}`;
    if (requestData.PackageId) {
      url += `&packageId=${requestData.PackageId}`;
    }
    if (requestData?.Sort?.Direction !== '') {
      url += `&sort.Column=${requestData?.Sort?.Column}&sort.Direction=${requestData?.Sort?.Direction}`;
    }

    return this.http.get<IBucketItemsResponse>(url);
  }
  public CreateFolder(requestData: ICreateFolderRequest): Observable<boolean> {
    const url = `${this.baseUrl}/create-folder`;
    return this.http.post<boolean>(url, requestData);
  }
  public UploadItem(requestFormData: FormData): Observable<string[] | null> {
    const url = `${this.baseUrl}/upload-items`;
    return this.http.post<string[] | null>(url, requestFormData, {
      params: { blob: "1" },
      responseType: "json",
    });
  }

  public CreateFoldersForFile(request: ICommitRequest): Observable<boolean> {
    const url = `${this.baseUrl}/create-folders-for-file`;
    return this.http.post<boolean>(url, request);
  }

  public Commit(requestData: any): Observable<any> {
    const url = `${this.baseUrl}/commit`;
    return this.http.post(url, requestData);
  }

  public TransferPackage(
    requestData: IBucketItemTransferRequest
  ): Observable<Partial<ITransferPackageResponse>> {
    const url = `${this.baseUrl}/transfer-package-folder`;
    return this.http.post<Partial<ITransferPackageResponse>>(url, requestData);
  }

  public UpdateStudyCompletionDate(
    requestData: IUpdateStudyCompletionDateRequest
  ): Observable<IUpdateStudyCompletionDateResponse> {
    const url = `${this.baseUrl}/update-study-completion-date`;
    return this.http.post<IUpdateStudyCompletionDateResponse>(url, requestData);
  }

  public RegenerateChecksum(
    regenerateChecksum: Partial<IRegenerateChecksum>
  ): Observable<boolean> {
    const url = `${this.baseUrl}/re-generate-checksum`;
    return this.http.post<boolean>(url, JSON.stringify(regenerateChecksum));
  }

  public downloadBucketItem(
    downloadItem: Partial<IDownloadItem>
  ): Observable<HttpResponse<Blob>> {
    const url = `${this.baseUrl}/download`;
    return this.http.post<Blob>(url, downloadItem, {
      headers: new HttpHeaders({
        Authorization: "{withCredentials: true}",
        "Content-Type": "application/json",
      }),
      observe: "response",
      responseType: "blob" as "json",
    });
  }

  public downloadAuditTrailEntry(
    payload: IDownloadAuditTrailEntry
  ): Observable<void> {
    const url = `${this.baseUrl}/download-audit-trail-entry`;
    return this.http.post<void>(url, payload);
  }

  public exportContentList(
    downloadItem: Partial<IExportContentListRequest>
  ): Observable<HttpResponse<Blob>> {
    const url = `${this.baseUrl}/export-content-list`;
    return this.http.post<Blob>(url, downloadItem, {
      headers: new HttpHeaders({
        Authorization: "{withCredentials: true}",
        "Content-Type": "application/json",
      }),
      observe: "response",
      responseType: "blob" as "json",
    });
  }

  public RenameFolder(requestData: IRenameFolderRequest): Observable<IRenameFolderResponse> {
    const url = `${this.baseUrl}/rename-folder`;
    return this.http.post<IRenameFolderResponse>(url, requestData);
  }

  public GetBucketItemsForMove(request: IBucketItemRequestForMove) {
    return this.http.get<any>(
      `${this.baseUrl}/get-bucket-items-for-move?bucketId=${request.BucketId
      }&buCode=${request.BUCode}&bucketType=${request.BucketType
      }&directoryName=${encodeURIComponent(
        request.DirectoryName
      )}&sourceDirectoryName=${request.SourceDirectoryName}`
    );
  }

  public MoveItem(requestData: IMoveItems): Observable<Partial<IMoveResponse>> {
    const url = `${this.baseUrl}/move-bucket-items`;
    return this.http.post<Partial<IMoveResponse>>(url, requestData);
  }

  public exportAuditTrail(
    bucketItemId: string,
    packageId: string,
    dateFormat: string
  ): Observable<HttpResponse<Blob>> {
    const url = `${this.baseUrl}/export-audit-trail?bucketItemId=${bucketItemId}&packageId=${packageId}&dateFormat=${dateFormat}`;
    return this.http.get<Blob>(url, {
      headers: new HttpHeaders({
        Authorization: "{withCredentials: true}",
        "Content-Type": "application/json",
      }),
      observe: "response",
      responseType: "blob" as "json",
    });
  }

  public IsGreaterThanMillionEntries(
    id: string,
    packageId: string
  ): Observable<boolean> {
    const url = `${this.baseUrl}/count-audit-trail-items?id=${id}&packageId=${packageId}`;
    return this.http.get<boolean>(url);
  }

  public CheckChildItemsCount(
    id: string,
    packageId: string
  ): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.baseUrl}/check-child-items-count?id=${id}&packageId=${packageId}`
    );
  }

  public DeleteFolder(requestData: IDeleteFolderRequest): Observable<boolean> {
    const url = `${this.baseUrl}/delete-empty-folder`;
    return this.http.post<boolean>(url, requestData);
  }

  public GetFileVersionHistory(
    requestData: Partial<IFileVersionHistoryRequest>
  ): Observable<Partial<IVersion>[]> {
    const url = `${this.baseUrl}/get-file-version-history?BucketItemId=${requestData.BucketItemId}&PackageId=${requestData.PackageId}&BUCode=${requestData.BUCode}&BucketType=${requestData.BucketType}&IsFromRequesterPage=${requestData.IsFromRequesterPage ? requestData.IsFromRequesterPage : false}`;
    return this.http.get<Partial<IVersion>[]>(url);
  }

  public UpdateTags(request: Partial<IUpdateTag>): Observable<IUpdateTagResponse> {
    const url = `${this.baseUrl}/update-tags`;
    return this.http.put<IUpdateTagResponse>(url, request);
  }

  public Search(data: Partial<ISearchRequest>): Observable<Partial<ISearchResultsResponse>> {
    const url = `${this.baseUrl}/get-search-results`;
    return this.http.post(url, data);
  }

  public RegenerateChecksumForFieVersion(requestData: Partial<IFileVersionChecksum>): Observable<boolean> {
    const url = `${this.baseUrl}/regenerate-checksum-for-file-version`;
    return this.http.post<boolean>(url, requestData).pipe(shareReplay());
  }

  public DownloadFileVersion(requestData: Partial<IDownloadFileversion>): Observable<HttpResponse<Blob>> {
    const url = `${this.baseUrl}/download-file-version`;
    return this.http.post<Blob>(url, requestData, {
      headers: new HttpHeaders({
        Authorization: "{withCredentials: true}",
        "Content-Type": "application/json",
      }),
      observe: "response",
      responseType: "blob" as "json",
    }).pipe(shareReplay());
  }

  public GetAuditRecords(data: Partial<IAuditTrailRequest>): Observable<IAuditsResponse> {
    const url = `${this.baseUrl}/get-audit-trail-search-results`;
    return this.http.post<IAuditsResponse>(url, data);
  }

  public DownloadAuditTrailCsv(data: Partial<IAuditTrailRequest>): Observable<HttpResponse<Blob>> {
    const url = `${this.baseUrl}/download-audit-trail`;
    return this.http.post<Blob>(url, data, {
      headers: new HttpHeaders({
        Authorization: "{withCredentials: true}",
        "Content-Type": "application/json",
      }),
      observe: "response",
      responseType: "blob" as "json",
    });
  }

  public LockPackage(packageId: string, locktransactionId: string): Observable<boolean> {
    const url = `${this.baseUrl}/lock-package`;
    return this.http.post<boolean>(url, { packageId, locktransactionId });
  }
  public UnlockPackage(packageId: string, locktransactionId: string): Observable<boolean> {
    const url = `${this.baseUrl}/unlock-package`;
    return this.http.post<boolean>(url, { packageId, locktransactionId });
  }

  public registerLargeFileDownload(
    payload: ILargeFileDownloadExpandedRequest
  ): Observable<ILargeFileDownloadResponse> {
    const url = `${this.largeFileDownloadUrl}/register-large-file-download-request`;
    return this.http.post<ILargeFileDownloadResponse>(url, payload);
  }

  public GetMaliciousFiles(): Observable<IMaliciousFilesResponse[]> {
    return this.http.get<IMaliciousFilesResponse[]>(
    `${this.baseUrl}/get-malicious-files`
    );
  }

  public DownloadMaliciousFiles(requestData: IDownloadMaliciousFilesRequestDto): Observable<boolean> {
    const url = `${this.baseUrl}/download-malicious-files`;
    return this.http.post<boolean>(url, requestData);
  }

  public GetTemplates(templateType: string): Observable<ITemplates[]> {
    const url = `${this.baseUrl}/get-template-details`;
    const params = new HttpParams().set('templateType', templateType);
    return this.http.get<any[]>(url, { params }).pipe(
      map((response: any[]) => {
        return response.map(item => ({
          TemplateId: item.TemplateId,
          TemplateName: item.TemplateName,
          TemplateStructure: item.FolderStructure ? [item.FolderStructure] : []
        }));
      })
    );
  }

  public downloadTemplate(bucketOrTemplateId: string, isTemplateId: boolean = true): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>(`${this.baseUrl}/download-template?bucketItemIdOrTemplateId=${bucketOrTemplateId}&isTemplateId=${isTemplateId}`, {
      observe: 'response',
      responseType: 'blob' as 'json'
    });
  }

  public LoadTemplate(request: {
    templateId: string;
    bucketId: string;
    isBucketItem: boolean;
    dirName?: string;
    studyFolderName?: string;
    bucketType?: string;
    buCode?: string;
  }): Observable<any> {
    const params = new URLSearchParams();
    params.append('templateId', request.templateId);
    params.append('bucketId', request.bucketId);
    params.append('isBucketItem', request.isBucketItem.toString());
    
    if (request.isBucketItem && request.dirName) {
      params.append('dirName', request.dirName);
    }
    if (request.studyFolderName) {
      params.append('studyFolderName', request.studyFolderName);
    }
    if (request.bucketType) {
      params.append('bucketType', request.bucketType);
    }
    params.append('buCode', request.buCode || '');
    
    return this.http.get<any>(`${this.baseUrl}/load-template?${params.toString()}`);
  }

  public SavePackageTags(request : Partial<ITags>[]) {
    this.http.put(`${this.baseUrl}/`, request).subscribe({
      next: (response) => {
        console.log('Package tags saved successfully:', response);
      },
      error: () => {
      }
    });
  }

}
